import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  formatMeterRaw,
  formatTradingDay,
} from './dailyDataUtils'
import DailyDataNav from './DailyDataNav'
import MeterReconciliationPanel from './MeterReconciliationPanel'
import DailyTurnoverCharts, { MonthTurnoverChart } from './DailyTurnoverCharts'
import TurnoverFocusSessions from './TurnoverFocusSessions'
import SlotAnalysisPanel from './SlotAnalysisPanel'
import { fetchMeterReconciliation, reconChartFilters as reconFilters } from './meterReconciliationApi'

import { formatSydneyDate, sydneyNowAsLocalDate } from '../../utils/sydneyDateTime'
const SUMMARY_LIMIT = 400
const MONTHS_BACK = 12

async function fetchReconciliation(tradingDay, filters = {}) {
  return fetchMeterReconciliation(tradingDay, filters)
}

async function fetchDaySummaries({ from, to, limit = SUMMARY_LIMIT } = {}) {
  const params = new URLSearchParams({ limit: String(limit) })
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  const res = await authFetch(getLocalApiUrl(`/api/daily-data/meter/day-summaries?${params}`))
  const json = await res.json()
  if (!json.success) throw new Error(json.error || 'Failed to load day summaries')
  return Array.isArray(json.data?.rows) ? json.data.rows : []
}

function dayKey(value) {
  if (!value) return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  return String(value).slice(0, 10)
}

function pctLabel(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toFixed(1)}%`
}

function localIsoDate(d = sydneyNowAsLocalDate()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function shiftMonth(ym, delta) {
  const [y, m] = String(ym).split('-').map(Number)
  const dt = new Date(y, m - 1 + delta, 1)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
}

function monthKeysFrom(anchorIso, count = MONTHS_BACK) {
  const start = String(anchorIso || localIsoDate()).slice(0, 7)
  const keys = []
  for (let i = 0; i < count; i += 1) keys.push(shiftMonth(start, -i))
  return keys
}

function formatMonthShort(ym) {
  const [y, m] = String(ym).split('-').map(Number)
  if (!y || !m) return ym
  return formatSydneyDate(new Date(y, m - 1, 1))
}

function formatDayShort(iso) {
  if (!iso) return '—'
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
  })
}

function sumRows(rows) {
  let turnover = 0
  let cancelcredit = 0
  let carded = 0
  let uncarded = 0
  let daysWithMeter = 0
  for (const r of rows || []) {
    const t = Number(r.turnover)
    if (Number.isFinite(t)) {
      turnover += t
      daysWithMeter += 1
    }
    const cc = Number(r.cancelcredit)
    if (Number.isFinite(cc)) cancelcredit += cc
    const c = Number(r.carded_turnover)
    if (Number.isFinite(c)) carded += c
    const u = Number(r.uncarded_turnover)
    if (Number.isFinite(u)) uncarded += u
  }
  const attributed = carded + uncarded
  return {
    days: (rows || []).length,
    daysWithMeter,
    turnover,
    cancelcredit,
    carded,
    uncarded,
    cardedPct: attributed > 0 ? (carded / attributed) * 100 : null,
  }
}

export default function DailyTurnover() {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlDay = searchParams.get('day') || ''
  const urlMonth = /^\d{4}-\d{2}$/.test(searchParams.get('month') || '')
    ? searchParams.get('month')
    : ''

  const [todayReport, setTodayReport] = useState(null)
  const [yesterdayReport, setYesterdayReport] = useState(null)
  const [otherReport, setOtherReport] = useState(null)
  const [loadingBootstrap, setLoadingBootstrap] = useState(true)
  const [loadingOther, setLoadingOther] = useState(false)
  const [error, setError] = useState(null)
  const [tradingDay, setTradingDay] = useState(urlDay)
  const [selectedMonth, setSelectedMonth] = useState(urlDay ? '' : urlMonth)
  const [clearingDay, setClearingDay] = useState(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [summaryRows, setSummaryRows] = useState([])
  const [loadingSummaries, setLoadingSummaries] = useState(false)
  const [openMonths, setOpenMonths] = useState(() => new Set())
  const [machineClass, setMachineClass] = useState('all')
  const [selectedEgmpks, setSelectedEgmpks] = useState([])
  const [selectedHours, setSelectedHours] = useState([])

  const activeFilters = useMemo(
    () => reconFilters({ machineClass, selectedEgmpks, selectedHours }),
    [machineClass, selectedEgmpks, selectedHours]
  )

  const today = todayReport?.trading_day || ''
  const yesterday = yesterdayReport?.trading_day || ''
  const timezone = todayReport?.timezone || yesterdayReport?.timezone || otherReport?.timezone

  const monthBuckets = useMemo(() => {
    const byMonth = new Map()
    for (const row of summaryRows) {
      const day = dayKey(row.trading_day)
      if (!day) continue
      const ym = day.slice(0, 7)
      if (!byMonth.has(ym)) byMonth.set(ym, [])
      byMonth.get(ym).push(row)
    }
    const keys = monthKeysFrom(today || yesterday || localIsoDate())
    return keys.map((ym) => {
      const days = byMonth.get(ym) || []
      return { ym, days, totals: sumRows(days) }
    })
  }, [summaryRows, today, yesterday])

  const selectedMonthBucket = useMemo(
    () => monthBuckets.find((b) => b.ym === selectedMonth) || null,
    [monthBuckets, selectedMonth]
  )
  const monthTotals = selectedMonthBucket?.totals || null

  const selectedReport = useMemo(() => {
    if (!tradingDay) return null
    if (tradingDay === today) return todayReport
    if (tradingDay === yesterday) return yesterdayReport
    if (otherReport?.trading_day === tradingDay) return otherReport
    return null
  }, [tradingDay, today, yesterday, todayReport, yesterdayReport, otherReport])

  useEffect(() => {
    const recon = selectedReport?.reconciliation
    const day = dayKey(selectedReport?.trading_day)
    const totals = recon?.totals
    if (!day || totals?.turnover == null) return
    setSummaryRows((prev) => {
      const idx = prev.findIndex((row) => dayKey(row.trading_day) === day)
      if (idx < 0) return prev
      const row = prev[idx]
      if (Number(row.turnover) === Number(totals.turnover)) return prev
      const next = prev.slice()
      next[idx] = {
        ...row,
        turnover: totals.turnover,
        creditwin: totals.creditwin ?? row.creditwin,
        cancelcredit: totals.cancelcredit ?? row.cancelcredit,
        cashin: totals.cashin ?? row.cashin,
        cashlessout: totals.cashlessout ?? row.cashlessout,
        distinct_egms: recon.completeness?.distinct_egms ?? row.distinct_egms,
        total_snapshots: recon.completeness?.total_snapshots ?? row.total_snapshots,
        partial_day: recon.completeness?.partial_day ?? row.partial_day,
        reset_egm_count: recon.resets?.egm_count ?? row.reset_egm_count,
        reset_event_count: recon.resets?.event_count ?? row.reset_event_count,
      }
      return next
    })
  }, [selectedReport])

  const pinTurnover = (report) => {
    const n = report?.reconciliation?.completeness?.distinct_egms || 0
    if (!n) return null
    return report?.reconciliation?.totals?.turnover
  }

  const loadSummaries = useCallback(async () => {
    setLoadingSummaries(true)
    try {
      const to = localIsoDate()
      const fromYm = monthKeysFrom(to).slice(-1)[0]
      const from = `${fromYm}-01`
      const rows = await fetchDaySummaries({ from, to, limit: SUMMARY_LIMIT })
      setSummaryRows(rows)
      return rows
    } catch (e) {
      console.warn('day summaries unavailable', e)
      setSummaryRows([])
      return []
    } finally {
      setLoadingSummaries(false)
    }
  }, [])

  const loadBootstrap = useCallback(async () => {
    setLoadingBootstrap(true)
    setError(null)
    try {
      const [todayData, yesterdayData] = await Promise.all([
        fetchReconciliation(null, 'all'),
        fetchReconciliation('yesterday', 'all'),
      ])
      setTodayReport(todayData)
      setYesterdayReport(yesterdayData)
      setTradingDay((prev) => {
        if (prev) return prev
        const monthQ = new URLSearchParams(window.location.search).get('month')
        if (monthQ && /^\d{4}-\d{2}$/.test(monthQ)) return ''
        return yesterdayData?.trading_day || todayData?.trading_day || ''
      })
      await loadSummaries()
      return { todayData, yesterdayData }
    } catch (e) {
      setError(e.message)
      setTodayReport(null)
      setYesterdayReport(null)
      return null
    } finally {
      setLoadingBootstrap(false)
    }
  }, [loadSummaries])

  useEffect(() => {
    loadBootstrap()
  }, [loadBootstrap])

  const accordionSeeded = useRef(false)
  useEffect(() => {
    if (!today && !tradingDay && !selectedMonth) return
    setOpenMonths((existing) => {
      const next = new Set(existing)
      if (!accordionSeeded.current) {
        const current = (today || localIsoDate()).slice(0, 7)
        next.add(current)
        accordionSeeded.current = true
      }
      if (tradingDay) next.add(tradingDay.slice(0, 7))
      if (selectedMonth) next.add(selectedMonth)
      return next
    })
  }, [today, tradingDay, selectedMonth])

  useEffect(() => {
    if (tradingDay) {
      setSearchParams({ day: tradingDay }, { replace: true })
      return
    }
    if (selectedMonth) {
      setSearchParams({ month: selectedMonth }, { replace: true })
    }
  }, [tradingDay, selectedMonth, setSearchParams])

  useEffect(() => {
    if (loadingBootstrap) return
    let cancelled = false
    ;(async () => {
      setError(null)
      try {
        if (!tradingDay) {
          const [todayData, yesterdayData] = await Promise.all([
            fetchReconciliation(null, activeFilters),
            fetchReconciliation('yesterday', activeFilters),
          ])
          if (!cancelled) {
            setTodayReport(todayData)
            setYesterdayReport(yesterdayData)
          }
          return
        }
        if (tradingDay === today) {
          const data = await fetchReconciliation(null, activeFilters)
          if (!cancelled) {
            setTodayReport(data)
            setOtherReport(null)
          }
          return
        }
        if (tradingDay === yesterday) {
          const data = await fetchReconciliation('yesterday', activeFilters)
          if (!cancelled) {
            setYesterdayReport(data)
            setOtherReport(null)
          }
          return
        }
        setLoadingOther(true)
        const data = await fetchReconciliation(tradingDay, activeFilters)
        if (!cancelled) setOtherReport(data)
      } catch (e) {
        if (!cancelled) {
          setError(e.message)
          if (tradingDay && tradingDay !== today && tradingDay !== yesterday) setOtherReport(null)
        }
      } finally {
        if (!cancelled && tradingDay && tradingDay !== today && tradingDay !== yesterday) {
          setLoadingOther(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeFilters, loadingBootstrap, tradingDay, today, yesterday])

  const resetFocusFilters = useCallback(() => {
    setSelectedEgmpks([])
    setSelectedHours([])
  }, [])

  const toggleSelectedEgm = useCallback((egmpk) => {
    const key = String(egmpk || '').trim()
    if (!key) return
    setSelectedEgmpks((prev) => (
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    ))
  }, [])

  const toggleSelectedHour = useCallback((hour) => {
    const h = Number(hour)
    if (!Number.isFinite(h) || h < 0 || h > 23) return
    setSelectedHours((prev) => (
      prev.includes(h)
        ? prev.filter((x) => x !== h)
        : [...prev, h].sort((a, b) => a - b)
    ))
  }, [])

  const selectDay = (day) => {
    if (!day) return
    setTradingDay(day)
    setSelectedMonth('')
    setMachineClass('all')
    setSelectedEgmpks([])
    setSelectedHours([])
    setOpenMonths((prev) => new Set(prev).add(day.slice(0, 7)))
  }

  const selectMonth = (ym) => {
    if (!ym) return
    setSelectedMonth(ym)
    setTradingDay('')
    setOpenMonths((prev) => new Set(prev).add(ym))
  }

  const toggleMonth = (ym, event) => {
    event?.stopPropagation()
    setOpenMonths((prev) => {
      const next = new Set(prev)
      if (next.has(ym)) next.delete(ym)
      else next.add(ym)
      return next
    })
  }

  const handleRefresh = async () => {
    const current = tradingDay
    const month = selectedMonth
    await loadSummaries()
    if (month && !current) {
      setSelectedMonth(month)
      setTradingDay('')
      return
    }
    if (!current) {
      setLoadingBootstrap(true)
      try {
        const [todayData, yesterdayData] = await Promise.all([
          fetchReconciliation(null, activeFilters),
          fetchReconciliation('yesterday', activeFilters),
        ])
        setTodayReport(todayData)
        setYesterdayReport(yesterdayData)
      } catch (e) {
        setError(e.message)
      } finally {
        setLoadingBootstrap(false)
      }
      return
    }
    setLoadingOther(true)
    try {
      let data
      if (current === today) data = await fetchReconciliation(null, activeFilters)
      else if (current === yesterday) data = await fetchReconciliation('yesterday', activeFilters)
      else data = await fetchReconciliation(current, activeFilters)
      if (current === today) setTodayReport(data)
      else if (current === yesterday) setYesterdayReport(data)
      else setOtherReport(data)
      setTradingDay(current)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingOther(false)
    }
  }

  /** Section-level refresh: reload only the selected day's report (Full reconciliation panel). */
  const refreshSelectedDay = async () => {
    const current = tradingDay
    if (!current || loadingOther) return
    setLoadingOther(true)
    try {
      let data
      if (current === today) data = await fetchReconciliation(null, activeFilters)
      else if (current === yesterday) data = await fetchReconciliation('yesterday', activeFilters)
      else data = await fetchReconciliation(current, activeFilters)
      if (current === today) setTodayReport(data)
      else if (current === yesterday) setYesterdayReport(data)
      else setOtherReport(data)
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingOther(false)
    }
  }

  const handleClearDay = async () => {
    if (!tradingDay) return
    setClearingDay(tradingDay)
    setShowClearConfirm(false)
    try {
      const params = new URLSearchParams({ trading_day: tradingDay })
      const res = await authFetch(getLocalApiUrl(`/api/daily-data/meter/clear-day?${params}`), {
        method: 'DELETE',
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to clear data')
      const parts = []
      if (json.meter_deleted) parts.push(`${json.meter_deleted} meter readings`)
      if (json.slot_deleted) parts.push(`${json.slot_deleted} slot analysis records`)
      if (json.import_runs_deleted) parts.push(`${json.import_runs_deleted} import runs`)
      if (json.combined?.enriched_deleted) parts.push(`${json.combined.enriched_deleted} enriched`)
      if (json.combined?.attribution_deleted) parts.push(`${json.combined.attribution_deleted} attribution`)
      const rollupN = (json.combined?.txn_rollup_deleted || 0) + (json.combined?.member_stats_deleted || 0)
      if (rollupN) parts.push(`${rollupN} rollup rows`)
      const msg = parts.length > 0
        ? `✓ Cleared ${tradingDay}: ${parts.join(', ')}`
        : `✓ ${json.message || 'Data cleared'}`
      alert(msg)
      await handleRefresh()
    } catch (e) {
      alert(`Failed to clear data: ${e.message}`)
    } finally {
      setClearingDay(null)
    }
  }

  const hasSelectedData = (selectedReport?.reconciliation?.completeness?.distinct_egms || 0) > 0
  const selectedTurnover = selectedReport?.reconciliation?.totals?.turnover
  const loadingSelected =
    loadingBootstrap ||
    (loadingOther && tradingDay && tradingDay !== today && tradingDay !== yesterday)

  const summaryForDay = (day) => summaryRows.find((r) => dayKey(r.trading_day) === day)

  const allocateLabel = summaryForDay(tradingDay)?.last_allocate_at
    ? 'Allocated'
    : summaryForDay(tradingDay)
      ? 'Meter only — allocate not run'
      : null

  return (
    <div className="space-y-3">
      <DailyDataNav />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Daily turnover</h1>
          <p className="text-[11px] text-gray-500">
            {timezone || '—'}
            {' · '}
            <Link to="/monitor-safe/daily-data/meter" className="text-primary-600 hover:underline">
              Meter import
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loadingBootstrap || loadingOther}
          className="inline-flex items-center px-2.5 py-1.5 border rounded-md text-xs hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-3.5 h-3.5 mr-1 ${loadingBootstrap || loadingOther ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] gap-4 items-start">
        <aside className="bg-white shadow rounded-lg border border-gray-200 lg:sticky lg:top-4 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto">
          <div className="px-2.5 py-2 border-b border-gray-100">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Trading days</p>
          </div>

          <div className="p-1.5 space-y-0.5 border-b border-gray-100">
            {[
              { label: 'Today', day: today, report: todayReport },
              { label: 'Yesterday', day: yesterday, report: yesterdayReport },
            ].map((pin) => {
              const selected = !!pin.day && pin.day === tradingDay
              const to = pinTurnover(pin.report)
              return (
                <button
                  key={pin.label}
                  type="button"
                  disabled={!pin.day || loadingBootstrap}
                  onClick={() => selectDay(pin.day)}
                  className={`w-full text-left rounded-md px-2 py-1 text-sm disabled:opacity-50 ${
                    selected
                      ? 'bg-indigo-50 text-indigo-900 ring-1 ring-indigo-200'
                      : 'hover:bg-gray-50 text-gray-800'
                  }`}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate">
                      <span className="font-medium">{pin.label}</span>
                      {pin.day && (
                        <span className="ml-1 text-[11px] text-gray-500 font-normal">{formatDayShort(pin.day)}</span>
                      )}
                    </span>
                    <span className="tabular-nums text-[11px] text-gray-600 shrink-0">
                      {loadingBootstrap && !pin.report ? '…' : to != null ? formatMeterRaw(to) : '—'}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>

          <div className="p-2">
            {loadingSummaries && summaryRows.length === 0 ? (
              <p className="px-2 py-3 text-sm text-gray-500">Loading days…</p>
            ) : (
              monthBuckets.map((bucket) => {
                const open = openMonths.has(bucket.ym)
                const monthSelected = !tradingDay && selectedMonth === bucket.ym
                return (
                  <div key={bucket.ym} className="mb-0.5">
                    <div
                      className={`flex items-stretch rounded-md ${
                        monthSelected ? 'bg-teal-50 ring-1 ring-teal-200' : ''
                      }`}
                    >
                      <button
                        type="button"
                        onClick={(e) => toggleMonth(bucket.ym, e)}
                        className="px-1.5 text-gray-500 hover:text-gray-800"
                        aria-expanded={open}
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${formatMonthShort(bucket.ym)}`}
                      >
                        {open ? (
                          <ChevronDownIcon className="w-4 h-4" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => selectMonth(bucket.ym)}
                        className="flex-1 min-w-0 text-left px-1 py-1"
                      >
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {formatMonthShort(bucket.ym)}
                          </span>
                          <span className="tabular-nums text-[11px] text-gray-500 shrink-0">
                            {bucket.totals.daysWithMeter
                              ? formatMeterRaw(bucket.totals.turnover)
                              : '—'}
                          </span>
                        </span>
                      </button>
                    </div>
                    {open && (
                      <div className="ml-4 mt-0.5 mb-1 space-y-0.5">
                        {bucket.days.length === 0 ? (
                          <p className="px-2 py-1 text-[11px] text-gray-400">No ingest this month</p>
                        ) : (
                          bucket.days.map((row) => {
                            const day = dayKey(row.trading_day)
                            const selected = day === tradingDay
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => selectDay(day)}
                                className={`w-full text-left rounded-md px-2 py-1 text-sm ${
                                  selected
                                    ? 'bg-indigo-50 text-indigo-900 ring-1 ring-indigo-200'
                                    : 'hover:bg-gray-50 text-gray-800'
                                }`}
                              >
                                <span className="flex items-baseline justify-between gap-2">
                                  <span>
                                    <span className="font-medium">{formatDayShort(day)}</span>
                                    {row.partial_day ? (
                                      <span className="ml-1 text-[10px] text-amber-700">partial</span>
                                    ) : null}
                                  </span>
                                  <span className="tabular-nums text-xs text-gray-600">
                                    {row.turnover != null ? formatMeterRaw(row.turnover) : '—'}
                                  </span>
                                </span>
                              </button>
                            )
                          })
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </aside>

        <div className="space-y-3 min-w-0 mt-4 lg:mt-0">
          {!tradingDay && selectedMonth && monthTotals && (
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-2xl font-bold text-gray-900 tabular-nums">
                  {monthTotals.daysWithMeter ? formatMeterRaw(monthTotals.turnover) : '—'}
                </p>
                <p className="text-sm text-gray-600">
                  {formatMonthShort(selectedMonth)}
                  {' · '}
                  {monthTotals.daysWithMeter} day{monthTotals.daysWithMeter === 1 ? '' : 's'}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                  <div className="text-[10px] text-gray-500 uppercase">Cancel credit</div>
                  <div className="text-sm font-semibold tabular-nums">
                    {formatMeterRaw(monthTotals.cancelcredit)}
                  </div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                  <div className="text-[10px] text-gray-500 uppercase">Carded</div>
                  <div className="text-sm font-semibold tabular-nums">
                    {monthTotals.carded > 0 || monthTotals.uncarded > 0
                      ? formatMeterRaw(monthTotals.carded)
                      : '—'}
                  </div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                  <div className="text-[10px] text-gray-500 uppercase">Uncarded</div>
                  <div className="text-sm font-semibold tabular-nums">
                    {monthTotals.carded > 0 || monthTotals.uncarded > 0
                      ? formatMeterRaw(monthTotals.uncarded)
                      : '—'}
                  </div>
                </div>
              </div>
              <MonthTurnoverChart days={selectedMonthBucket?.days || []} onSelectDay={selectDay} />
              {selectedMonthBucket?.days?.length > 0 && (
                <div className="overflow-x-auto border border-gray-200 rounded-lg mt-3">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Day</th>
                        <th className="px-2 py-1.5 text-right font-medium">Turnover</th>
                        <th className="px-2 py-1.5 text-right font-medium">EGMs</th>
                        <th className="px-2 py-1.5 text-right font-medium">Carded %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedMonthBucket.days.map((row) => {
                        const day = dayKey(row.trading_day)
                        return (
                          <tr
                            key={day}
                            onClick={() => selectDay(day)}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">
                              {formatTradingDay(day)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {row.turnover != null ? formatMeterRaw(row.turnover) : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{row.distinct_egms ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{pctLabel(row.carded_turnover_pct)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tradingDay && (
            <>
              <DailyTurnoverCharts
                charts={hasSelectedData ? selectedReport?.reconciliation?.charts : null}
                tradingDay={tradingDay}
                amount={selectedTurnover}
                amountLoading={loadingSelected}
                hasData={hasSelectedData}
                partialDay={!!selectedReport?.reconciliation?.completeness?.partial_day}
                allocateLabel={allocateLabel}
                machineClass={machineClass}
                machineClassCounts={selectedReport?.reconciliation?.charts?.machine_class_counts}
                onMachineClassChange={setMachineClass}
                selectedEgmpks={selectedEgmpks}
                selectedHours={selectedHours}
                onToggleEgm={toggleSelectedEgm}
                onToggleHour={toggleSelectedHour}
                onClearFocus={resetFocusFilters}
                onClear={() => setShowClearConfirm(true)}
                clearing={clearingDay}
              />

              <TurnoverFocusSessions
                tradingDay={tradingDay}
                machineClass={machineClass}
                selectedEgmpks={selectedEgmpks}
                selectedHours={selectedHours}
                allocateLabel={allocateLabel}
              />

              <MeterReconciliationPanel
                tradingDay={tradingDay}
                onTradingDayChange={selectDay}
                prefetchedReport={selectedReport}
                prefetchedLoading={loadingSelected || loadingOther}
                skipOwnFetch
                onRefresh={refreshSelectedDay}
                hideSlotAnalysis
                title="Full reconciliation"
                showDatePicker={false}
              />

              <SlotAnalysisPanel slot={selectedReport?.reconciliation?.slot_analysis ?? null} />
            </>
          )}

          {!tradingDay && !selectedMonth && (
            <div className="bg-white shadow rounded-lg p-5 text-sm text-gray-600">
              Select a day or a month on the left to see turnover.
            </div>
          )}
        </div>
      </div>

      {showClearConfirm && tradingDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear this trading day?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Clears the <strong className="text-gray-900">{formatTradingDay(tradingDay)}</strong> trading
                  day — venue clock {String(selectedReport?.reconciliation?.charts?.cutoff_hour ?? 3).padStart(2, '0')}:00
                  to the next day&apos;s cutoff
                  {timezone ? ` (${timezone})` : ''}. Overnight snapshots after midnight are part of this
                  day, even if they were tagged with the next calendar date. Sessions are kept.
                </p>
                {hasSelectedData && (
                  <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
                    <p className="text-gray-700">
                      <strong>This will delete:</strong>
                    </p>
                    <ul className="mt-1 space-y-1 text-gray-600">
                      <li>
                        • ~{selectedReport.reconciliation.completeness.distinct_egms || 0} EGMs worth of
                        meter readings in the trading-day window
                      </li>
                      <li>• All quarter-hour snapshots from cutoff to next cutoff</li>
                      <li>• Slot analysis data (if any)</li>
                      <li>• Import run history for this day</li>
                      <li>• Enriched sessions, combined attribution, and rollups</li>
                    </ul>
                  </div>
                )}
                <p className="text-xs text-gray-500">You can re-import the data later if needed.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearDay}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Yes, clear this day
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
